import React, {useState, useRef, useEffect, useCallback} from 'react';
import {
  StyleSheet,
  View,
  PixelRatio,
  Pressable,
  Platform,
  Modal,
} from 'react-native';
import {withForwardedNavigationParams} from 'react-navigation-props-mapper';
import NavActions from 'src/actions/NavActions';
import AppRoutes from 'src/AppRoutes';
import FirebaseAnalytic from '../../../nativeModules/FirebaseAnalytic';
import Settings from 'src/Settings';
import BottomSheet, {BottomSheetScrollView} from '@gorhom/bottom-sheet';
import Styles from 'src/components/Styles';
import {RewardCashType} from 'src/types/Rewards';
import CashRedeemTab from 'src/components/elements/rewards/CashRedeemTab';
import {
  withGlobalize,
  WithGlobalizeProps,
} from '../../../i18n/HOC/withGlobalize';

export type CashRewardRedeemScreenProps = WithGlobalizeProps & {
  cashReward: RewardCashType;
  pointsRequired: number;
};

enum BottomSheetState {
  NOT_READY = 'NOT_READY',
  FULLY_OPEN = 'FULLY_OPEN',
  FULLY_CLOSED = 'FULLY_CLOSED',
}

const CashRewardRedeemScreen: React.FC<CashRewardRedeemScreenProps> = (
  props,
) => {
  const [modalVisible, setModalVisible] = useState(Platform.OS === 'web');
  const bottomSheetRef = useRef<BottomSheet>(null);

  const bottomSheetCustomStates = {
    [-1]: BottomSheetState.FULLY_CLOSED,
    0: BottomSheetState.FULLY_OPEN,
    999: BottomSheetState.NOT_READY,
  };

  const bottomSheetCurrentState = useRef<BottomSheetState | undefined>(
    undefined,
  );

  useEffect(() => {
    FirebaseAnalytic.trackEvent('componentDidMount', 'CashRewardRedeemScreen', {
      ...props,
    });
    if (bottomSheetRef.current) {
      bottomSheetRef.current.close();
    }
  }, [props]);

  const trackOnCloseEvent = () => {
    FirebaseAnalytic.trackEvent('onClose', 'CashRewardRedeemScreen', {
      ...props,
      navigate: AppRoutes.Main,
    });
  };

  const handleNavigation = () => {
    if (Settings.isNewUI()) {
      NavActions.pop();
    } else {
      NavActions.navigate(AppRoutes.Main);
    }
  };

  const onClose = useCallback(() => {
    if (Platform.OS === 'web') {
      setModalVisible(false);
      trackOnCloseEvent();
      handleNavigation();
    } else {
      if (!bottomSheetRef.current) {
        return;
      }
      trackOnCloseEvent();
      handleNavigation();
    }
  }, []);

  const handleSheetChange = (index: number) => {
    bottomSheetCurrentState.current = bottomSheetCustomStates[index];
    if (index === -1) {
      onClose();
    }
  };

  const renderInner = () => (
    <CashRedeemTab cashReward={props.cashReward} point={props.pointsRequired} />
  );

  const renderModal = () => (
    <Modal
      animationType="fade"
      transparent={true}
      visible={modalVisible}
      onRequestClose={onClose}
    >
      <View style={styles.modalBackdrop}>
        <View style={styles.modalContainer}>{renderInner()}</View>
      </View>
    </Modal>
  );

  const renderBottomSheet = () => (
    <BottomSheet
      ref={bottomSheetRef}
      snapPoints={[430 + PixelRatio.getFontScale() * 35]}
      enableHandlePanningGesture={true}
      enablePanDownToClose={true}
      onChange={handleSheetChange}
      style={styles.bottomSheet}
      enableContentPanningGesture={true}
    >
      <BottomSheetScrollView>{renderInner()}</BottomSheetScrollView>
    </BottomSheet>
  );

  return (
    <View style={styles.container} testID="pressable-element">
      <Pressable
        nativeID="Btn-pressable-element"
        style={styles.contentParentView}
        onPress={() => {
          if (bottomSheetCurrentState.current === undefined) {
            NavActions.pop();
          } else if (bottomSheetRef.current) {
            bottomSheetRef.current.forceClose();
          }
        }}
      />
      {Platform.OS === 'web' ? renderModal() : renderBottomSheet()}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  contentContainer: {
    padding: 16,
  },
  contentParentView: {
    width: '100%',
    height: '60%',
    backgroundColor: 'transparent',
  },
  bottomSheet: {
    borderRadius: 32,
    overflow: 'hidden',
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  modalContainer: {
    width: '100%',
    maxWidth: 600,
    backgroundColor: Styles.Colors.PayNew.white01,
    borderRadius: Styles.Spacing.m3 + Styles.Spacing.m2,
    overflow: 'hidden',
    paddingHorizontal: Styles.Spacing.m3 + Styles.Spacing.m2,
  },
});

export default withForwardedNavigationParams()(
  withGlobalize(CashRewardRedeemScreen),
);
